import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DemoVideo from './DemoVideo';
import QRScanner from './QRScanner';

const Home = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [showDemoVideo, setShowDemoVideo] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const FEATURES_COUNT = 12;
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % FEATURES_COUNT);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const handleQRScan = (data) => {
        const { restaurantId, tableId } = data;
        setShowScanner(false);
        navigate(`/menu/${restaurantId}/${tableId}`);
    };

    const features = [
        {
            icon: "🤖",
            title: "AI-Powered Analytics",
            description: "Advanced machine learning algorithms provide deep insights into your restaurant operations, customer behavior, and sales patterns.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-100"
        },
        {
            icon: "📊",
            title: "Real-time Dashboard",
            description: "Monitor your restaurant's performance in real-time with comprehensive analytics, revenue tracking, and operational metrics.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        },
        {
            icon: "🪑",
            title: "Dine-in & Reservations",
            description: "Let customers book tables online, manage reservations, send confirmation and reminder emails, and assign tables with live status to avoid double-booking.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-200"
        },
        {
            icon: "🍽️",
            title: "Smart Menu Management",
            description: "Intelligent menu optimization with AI-driven recommendations, inventory tracking, and dynamic pricing strategies.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        },
        {
            icon: "📱",
            title: "Multi-Platform Access",
            description: "Access your restaurant data from anywhere with our responsive web and mobile applications.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-100"
        },
        {
            icon: "💰",
            title: "Revenue Optimization",
            description: "Maximize profits with predictive analytics, demand forecasting, and automated pricing adjustments.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        },
        {
            icon: "👥",
            title: "Customer Insights",
            description: "Understand your customers better with AI-powered analytics on ordering patterns and preferences.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-100"
        },
        {
            icon: "👷",
            title: "Staff Management & Attendance",
            description: "Track check-ins and check-outs, manage roles and permissions, and monitor staff performance—all in one place.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        },
        {
            icon: "🪑",
            title: "Table Management",
            description: "Assign and free tables, view live occupancy, generate QR codes for table ordering, and streamline waiter assignments.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-200"
        },
        {
            icon: "🎁",
            title: "Special Offers & Deals",
            description: "Create and run promotions, discounts, and combo deals. Apply offers at checkout and track redemption for better ROI.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        },
        {
            icon: "📦",
            title: "Inventory Management",
            description: "Track ingredients, recipes, and stock levels. Get low-stock alerts and link menu items to recipes for smarter ordering.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50 border-amber-100"
        },
        {
            icon: "📈",
            title: "Food Analytics",
            description: "See bestsellers, category performance, veg vs non-veg trends, and period-wise sales to optimize your menu and operations.",
            color: "from-amber-500 to-amber-600",
            bgColor: "bg-amber-50/80 border-amber-100"
        }
    ];

    const testimonials = [
        {
            name: "Sarah Chen",
            role: "Owner, Fusion Bistro",
            content: "RestroAI transformed our restaurant operations. The AI insights helped us increase revenue by 35% in just 3 months.",
            avatar: "👩‍🍳",
            rating: 5
        },
        {
            name: "Michael Rodriguez",
            role: "Manager, Urban Diner",
            content: "The real-time analytics and smart menu recommendations have made our decision-making process so much more efficient.",
            avatar: "👨‍💼",
            rating: 5
        },
        {
            name: "Emily Johnson",
            role: "CEO, Taste of India",
            content: "Finally, a restaurant management system that actually understands our business. The AI features are game-changing.",
            avatar: "👩‍💻",
            rating: 5
        }
    ];

    const aboutRestaurants = [
        { city: "Varanasi", restaurant: "Kashi Annapurna", owner: "Rajesh Gupta", feedback: "RestroAI has made our operations so smooth. Order management and billing are flawless even during peak hours at the ghats. Highly recommended!", rating: 5 },
        { city: "Lucknow", restaurant: "Royal Tunday", owner: "Amina Khan", feedback: "From inventory to customer insights, everything is at our fingertips. Revenue went up by 28% in the first quarter. Best decision we made.", rating: 5 },
        { city: "Patna", restaurant: "Pataliputra Kitchen", owner: "Vikash Kumar", feedback: "Real-time dashboard and staff management have transformed how we run the restaurant. Our team and customers are happier than ever.", rating: 5 },
        { city: "Bangalore", restaurant: "Tech Park Bistro", owner: "Priya Nair", feedback: "Perfect for a busy city like Bangalore. AI insights on peak hours and best-selling items helped us cut waste and boost profits. Game changer!", rating: 5 },
        { city: "Pune", restaurant: "FC Road Eats", owner: "Siddharth Deshpande", feedback: "Simple to use, powerful under the hood. We switched from spreadsheets to RestroAI and never looked back. Support team is excellent.", rating: 5 },
        { city: "Hyderabad", restaurant: "Nawab's Biryani House", owner: "Farhan Ahmed", feedback: "Handles our high volume and multiple outlets with ease. The analytics on regional preferences helped us tailor our menu. Outstanding software.", rating: 5 },
        { city: "Delhi", restaurant: "Chandni Chowk Delights", owner: "Neha Sharma", feedback: "We needed something that could keep up with Delhi's pace. RestroAI delivers—orders, tables, and bills all in one place. Customer satisfaction has never been higher.", rating: 5 }
    ];

    const stats = [
        { number: "500+", label: "Restaurants", icon: "🏪" },
        { number: "95%", label: "Satisfaction Rate", icon: "⭐" },
        { number: "40%", label: "Revenue Increase", icon: "📈" },
        { number: "24/7", label: "AI Support", icon: "🤖" }
    ];

    const floatingElements = [
        { icon: "🍕", delay: 0, duration: 6 },
        { icon: "🍔", delay: 1, duration: 8 },
        { icon: "🍜", delay: 2, duration: 7 },
        { icon: "🍣", delay: 3, duration: 9 },
        { icon: "🍰", delay: 4, duration: 6 },
        { icon: "☕", delay: 5, duration: 8 }
    ];

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900 relative overflow-hidden">
            {/* Background atmosphere */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100/30 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/25 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-1/4 left-0 w-[60vmax] h-[60vmax] bg-amber-100/30 rounded-full blur-[100px] translate-x-1/2" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.08),transparent)]" />
            </div>

            {/* Navigation */}
            <nav className="relative z-50 bg-white/80 backdrop-blur-xl border-b border-stone-200/80 sticky top-0">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center gap-2 py-5 min-w-0">
                        <motion.div 
                            className="flex items-center flex-shrink-0 min-w-0"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <img src="/logo-bright.svg" alt="RestroAI" className="h-7 sm:h-8 md:h-10 lg:h-12 w-auto max-w-[100px] sm:max-w-[120px] md:max-w-none" />
                        </motion.div>
                        <div className="hidden md:flex items-center space-x-8 flex-shrink-0">
                            {[
                                { label: 'Features', href: '#features' },
                                { label: 'About', href: '#about' },
                                { label: 'Contact', href: '/contact-us', isRoute: true }
                            ].map((item, index) => (
                                item.isRoute ? (
                                    <motion.div key={item.label} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }}>
                                        <Link to={item.href} className="text-stone-600 hover:text-stone-900 transition-colors relative group inline-block">
                                            {item.label}
                                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 group-hover:w-full transition-all duration-300"></span>
                                        </Link>
                                    </motion.div>
                                ) : (
                                    <motion.a
                                        key={item.label}
                                        href={item.href}
                                        className="text-stone-600 hover:text-stone-900 transition-colors relative group"
                                        initial={{ opacity: 0, y: -20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                    >
                                        {item.label}
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 group-hover:w-full transition-all duration-300"></span>
                                    </motion.a>
                                )
                            ))}
                            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}>
                                <Link to="/restaurants" className="text-stone-600 hover:text-stone-900 transition-colors relative group inline-flex items-center gap-1">
                                    <span>🍽️</span> Restaurants
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 group-hover:w-full transition-all duration-300"></span>
                                </Link>
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.38 }}>
                                <Link to="/login" className="text-sm font-semibold text-amber-600 hover:text-amber-700 px-5 py-2.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 transition-all duration-300">Login</Link>
                            </motion.div>
                        </div>
                        <motion.div 
                            className="md:hidden flex items-center gap-1 min-w-0 flex-shrink-0"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Link to="/restaurants" className="inline-flex items-center justify-center text-stone-600 hover:text-stone-900 text-xs font-medium px-2 py-1 rounded-full border border-stone-200 hover:border-stone-300 whitespace-nowrap shrink-0" title="Restaurants">🍽️ Restaurants</Link>
                            <Link to="/login" className="inline-flex items-center justify-center text-stone-600 hover:text-stone-900 text-xs font-medium px-2 py-1 rounded-full border border-stone-200 hover:border-stone-300 whitespace-nowrap shrink-0">Login</Link>
                            <Link to="/contact-us" className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-2 py-1 rounded-full transition-all duration-300 active:scale-95 shadow shadow-amber-500/20 whitespace-nowrap shrink-0" title="Start Free Trial">Start Trial</Link>
                        </motion.div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative overflow-hidden min-h-0 md:min-h-[90vh] flex items-center pt-6 pb-10 sm:pt-12 sm:pb-16 md:pt-20 md:pb-24">
                <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="mb-4 sm:mb-6 md:mb-8"
                        >
                            <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-amber-50 border border-amber-200/80 mb-3 sm:mb-6">
                                <span className="text-base sm:text-xl md:text-2xl mr-1.5 sm:mr-2">🚀</span>
                                <span className="text-stone-700 font-medium text-xs sm:text-sm md:text-base">The Future of Restaurant Management is Here</span>
                            </div>
                        </motion.div>

                        <motion.h1 
                            className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-stone-900 mb-3 sm:mb-6 leading-tight"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            The Future of{' '}
                            <motion.span 
                                className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 mt-0.5 sm:mt-1"
                                animate={{ opacity: [1, 1] }}
                                transition={{ duration: 3, repeat: Infinity }}
                            >
                                Restaurant Management With AI
                            </motion.span>
                        </motion.h1>
                        
                        <motion.p 
                            className="text-sm sm:text-base md:text-xl lg:text-2xl text-stone-600 mb-4 sm:mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed px-1"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            Powered by artificial intelligence, RestroAI revolutionizes how restaurants operate, 
                            analyze data, and serve customers with unprecedented efficiency and insights.
                        </motion.p>
                        
                        <motion.div 
                            className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            <motion.button 
                                onClick={() => setShowScanner(true)}
                                className="md:hidden w-full sm:w-auto border-2 border-amber-300 bg-amber-50 text-stone-800 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:bg-amber-100 transition-all duration-300 flex items-center justify-center space-x-2"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                                <span>Scan Restaurant QR</span>
                            </motion.button>
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto">
                                <Link to="/contact-us" className="block w-full text-center bg-amber-500 hover:bg-amber-600 text-white px-4 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-full font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30">Start Free Trial</Link>
                            </motion.div>
                            <motion.button 
                                onClick={() => setShowDemoVideo(true)}
                                className="border-2 border-stone-200 text-stone-700 px-4 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-full font-semibold text-sm sm:text-base md:text-lg hover:bg-stone-100 transition-all duration-300 group w-full sm:w-auto"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span className="flex items-center justify-center">▶️ Watch Demo <span className="ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform">→</span></span>
                            </motion.button>
                        </motion.div>

                        <motion.div 
                            className="mt-6 sm:mt-8 md:mt-12 flex flex-wrap justify-center items-center gap-3 sm:gap-6 md:gap-8 text-stone-500 text-xs sm:text-sm"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <div className="flex items-center"><span className="text-base sm:text-xl md:text-2xl mr-1.5 sm:mr-2">✅</span><span>Trusted by 500+ Restaurants</span></div>
                            <div className="flex items-center"><span className="text-base sm:text-xl md:text-2xl mr-1.5 sm:mr-2">🔒</span><span>Bank-Level Security</span></div>
                            <div className="flex items-center"><span className="text-base sm:text-xl md:text-2xl mr-1.5 sm:mr-2">⚡</span><span>99.9% Uptime</span></div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-8 sm:py-12 md:py-16 relative">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="text-center p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl bg-white border border-stone-200 shadow-sm hover:shadow-md hover:border-amber-100 transition-all duration-300 group"
                            >
                                <motion.div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>{stat.icon}</motion.div>
                                <motion.div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600 mb-0.5 sm:mb-1" initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, delay: index * 0.1 }} viewport={{ once: true }}>{stat.number}</motion.div>
                                <div className="text-stone-500 text-xs sm:text-sm font-medium">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section - Transform Your Restaurant with AI */}
            <section id="features" className="py-10 sm:py-14 md:py-20 relative scroll-mt-20">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <motion.div className="text-center mb-8 sm:mb-12 md:mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }} className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-amber-50 border border-amber-200 mb-3 sm:mb-6">
                            <span className="text-lg sm:text-xl md:text-2xl mr-1.5 sm:mr-2">✨</span>
                            <span className="text-stone-700 font-medium text-xs sm:text-sm md:text-base">AI-Powered Features</span>
                        </motion.div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-3 sm:mb-6">Transform Your Restaurant with AI</h2>
                        <p className="text-sm sm:text-base md:text-xl text-stone-600 max-w-2xl mx-auto px-1">Discover how artificial intelligence transforms every aspect of your restaurant operations—from analytics to dine-in reservations.</p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.08 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                                className={`rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border ${feature.bgColor} hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-300 cursor-pointer group`}
                                onClick={() => setActiveFeature(index)}
                            >
                                <motion.div className="text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300" whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>{feature.icon}</motion.div>
                                <h3 className="text-base sm:text-lg font-bold text-stone-900 mb-2 sm:mb-3">{feature.title}</h3>
                                <p className="text-stone-600 leading-relaxed text-xs sm:text-sm">{feature.description}</p>
                                <motion.div className={`mt-3 sm:mt-4 w-8 sm:w-10 h-0.5 sm:h-1 bg-gradient-to-r ${feature.color} rounded-full`} initial={{ width: 0 }} whileInView={{ width: 40 }} transition={{ duration: 0.5, delay: 0.3 + index * 0.05 }} viewport={{ once: true }} />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section – Restaurants using RestroAI */}
            <section id="about" className="py-10 sm:py-14 md:py-20 relative scroll-mt-20">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <motion.div className="text-center mb-6 sm:mb-10 md:mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-3 sm:mb-6">About RestroAI</h2>
                        <p className="text-sm sm:text-base md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed px-1">
                            Founded and developed by Ritesh Kumar Pandey, RestroAI is an AI-powered restaurant management platform trusted by eateries across India. 
                            We help owners streamline orders, manage tables, track inventory, dine-in reservations, and grow revenue with real-time 
                            analytics and smart insights—whether you run a single outlet or multiple locations.
                        </p>
                    </motion.div>
                    <motion.div className="text-center mb-6 sm:mb-8 md:mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                        <p className="text-xs sm:text-sm md:text-lg text-stone-600 font-medium px-1">Trusted by restaurants in Varanasi, Lucknow, Patna, Bangalore, Pune, Hyderabad & Delhi</p>
                    </motion.div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                        {aboutRestaurants.map((item, index) => (
                            <motion.div
                                key={item.city}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.08 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-stone-200 shadow-sm hover:shadow-md hover:border-amber-100 transition-all duration-300"
                            >
                                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                                    <span className="text-lg sm:text-xl md:text-2xl">🍽️</span>
                                    <span className="text-amber-600 text-xs sm:text-sm font-semibold uppercase tracking-wide">{item.city}</span>
                                </div>
                                <h3 className="text-stone-900 font-semibold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">{item.restaurant}</h3>
                                <p className="text-stone-500 text-xs sm:text-sm mb-2 sm:mb-4">{item.owner}, Owner</p>
                                <div className="flex mb-2 sm:mb-3">{[...Array(item.rating)].map((_, i) => (<span key={i} className="text-amber-400 text-xs sm:text-sm">⭐</span>))}</div>
                                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed italic">"{item.feedback}"</p>
                            </motion.div>
                        ))}
                        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} viewport={{ once: true }} className="bg-amber-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-dashed border-amber-200 flex flex-col items-center justify-center min-h-[140px] sm:min-h-[180px] md:min-h-[200px] text-center">
                            <span className="text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3">✨</span>
                            <h3 className="text-stone-900 font-semibold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">And many more</h3>
                            <p className="text-stone-500 text-xs sm:text-sm">Restaurants across India trust RestroAI</p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Interactive Demo Section */}
            <section className="py-10 sm:py-14 md:py-20 relative">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <motion.div className="text-center mb-8 sm:mb-12 md:mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-3 sm:mb-6">See RestroAI in Action</h2>
                        <p className="text-sm sm:text-base md:text-xl text-stone-600 max-w-2xl mx-auto px-1">Experience the power of AI-driven restaurant management</p>
                    </motion.div>

                    <motion.div 
                        className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-5 md:p-8 border border-stone-200 shadow-xl"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl relative overflow-hidden">
                            {/* Animated Background */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
                            
                            {/* Animated Dashboard Elements */}
                            <div className="absolute inset-0 p-3 sm:p-5 md:p-8">
                                {/* Header Bar */}
                                <motion.div 
                                    className="flex items-center justify-between mb-2 sm:mb-4 md:mb-6"
                                    initial={{ opacity: 0, y: -20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        <span className="text-white/60 text-sm ml-4">RestroAI Dashboard</span>
                                    </div>
                                    <div className="text-white/60 text-sm">Live Data</div>
                                </motion.div>

                                {/* Main Dashboard Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full">
                                    
                                    {/* Revenue Chart */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                        viewport={{ once: true }}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-white font-semibold">Revenue Analytics</h4>
                                            <span className="text-green-400 text-sm">+23%</span>
                                        </div>
                                        <div className="space-y-2">
                                            {[60, 80, 45, 90, 75, 85].map((height, index) => (
                                                <motion.div
                                                    key={index}
                                                    className="bg-gradient-to-t from-blue-500 to-cyan-400 rounded-sm"
                                                    initial={{ height: 0 }}
                                                    whileInView={{ height: `${height}%` }}
                                                    transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                                                    viewport={{ once: true }}
                                                    style={{ height: `${height}%` }}
                                                />
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* Order Status */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.4 }}
                                        viewport={{ once: true }}
                                    >
                                        <h4 className="text-white font-semibold mb-3">Live Orders</h4>
                                        <div className="space-y-2">
                                            {[
                                                { id: "#1234", status: "Preparing", time: "2 min" },
                                                { id: "#1235", status: "Ready", time: "0 min" },
                                                { id: "#1236", status: "Delivered", time: "5 min ago" }
                                            ].map((order, index) => (
                                                <motion.div
                                                    key={order.id}
                                                    className="flex items-center justify-between p-2 bg-white/5 rounded-lg"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.5, delay: 0.6 + index * 0.2 }}
                                                    viewport={{ once: true }}
                                                >
                                                    <span className="text-white/80 text-sm">{order.id}</span>
                                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                                        order.status === 'Ready' ? 'bg-green-500/20 text-green-400' :
                                                        order.status === 'Preparing' ? 'bg-yellow-500/20 text-yellow-400' :
                                                        'bg-blue-500/20 text-blue-400'
                                                    }`}>
                                                        {order.status}
                                                    </span>
                                                    <span className="text-white/60 text-xs">{order.time}</span>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* AI Insights */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.5 }}
                                        viewport={{ once: true }}
                                    >
                                        <h4 className="text-white font-semibold mb-3">AI Insights</h4>
                                        <div className="space-y-3">
                                            <motion.div
                                                className="flex items-center space-x-2"
                                                initial={{ opacity: 0, x: 20 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.5, delay: 0.7 }}
                                                viewport={{ once: true }}
                                            >
                                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                                <span className="text-white/80 text-sm">Peak hours: 7-9 PM</span>
                                            </motion.div>
                                            <motion.div
                                                className="flex items-center space-x-2"
                                                initial={{ opacity: 0, x: 20 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.5, delay: 0.8 }}
                                                viewport={{ once: true }}
                                            >
                                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                                <span className="text-white/80 text-sm">Best seller: Margherita Pizza</span>
                                            </motion.div>
                                            <motion.div
                                                className="flex items-center space-x-2"
                                                initial={{ opacity: 0, x: 20 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.5, delay: 0.9 }}
                                                viewport={{ once: true }}
                                            >
                                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                                                <span className="text-white/80 text-sm">Revenue up 15% this week</span>
                                            </motion.div>
                                        </div>
                                    </motion.div>

                                    {/* Menu Management */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.6 }}
                                        viewport={{ once: true }}
                                    >
                                        <h4 className="text-white font-semibold mb-3">Menu Performance</h4>
                                        <div className="space-y-2">
                                            {[
                                                { name: "Pizza Margherita", sales: 45, trend: "↗️" },
                                                { name: "Burger Deluxe", sales: 32, trend: "↗️" },
                                                { name: "Pasta Carbonara", sales: 28, trend: "↘️" }
                                            ].map((item, index) => (
                                                <motion.div
                                                    key={item.name}
                                                    className="flex items-center justify-between"
                                                    initial={{ opacity: 0, y: 10 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.5, delay: 1.0 + index * 0.1 }}
                                                    viewport={{ once: true }}
                                                >
                                                    <span className="text-white/80 text-sm truncate">{item.name}</span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-white/60 text-xs">{item.sales} sold</span>
                                                        <span className="text-lg">{item.trend}</span>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* Customer Analytics */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.7 }}
                                        viewport={{ once: true }}
                                    >
                                        <h4 className="text-white font-semibold mb-3">Customer Analytics</h4>
                                        <div className="text-center">
                                            <motion.div
                                                className="text-3xl font-bold text-white mb-2"
                                                initial={{ scale: 0 }}
                                                whileInView={{ scale: 1 }}
                                                transition={{ type: "spring", delay: 1.2 }}
                                                viewport={{ once: true }}
                                            >
                                                4.8⭐
                                            </motion.div>
                                            <div className="text-white/60 text-sm">Average Rating</div>
                                            <motion.div
                                                className="mt-3 text-2xl font-bold text-green-400"
                                                initial={{ opacity: 0 }}
                                                whileInView={{ opacity: 1 }}
                                                transition={{ duration: 0.5, delay: 1.3 }}
                                                viewport={{ once: true }}
                                            >
                                                156
                                            </motion.div>
                                            <div className="text-white/60 text-sm">Happy Customers Today</div>
                                        </div>
                                    </motion.div>

                                    {/* Real-time Notifications */}
                                    <motion.div 
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.8 }}
                                        viewport={{ once: true }}
                                    >
                                        <h4 className="text-white font-semibold mb-3">Live Updates</h4>
                                        <div className="space-y-2">
                                            {[
                                                "New order received! 🍕",
                                                "Table 5 is ready for service 🎉",
                                                "Inventory alert: Tomatoes running low ⚠️",
                                                "Customer review: 5 stars! ⭐"
                                            ].map((notification, index) => (
                                                <motion.div
                                                    key={index}
                                                    className="text-white/80 text-sm p-2 bg-white/5 rounded-lg"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.5, delay: 1.4 + index * 0.2 }}
                                                    viewport={{ once: true }}
                                                >
                                                    {notification}
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Floating Action Button */}
                                <motion.button
                                    className="absolute bottom-6 right-6 w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-amber-500/40 transition-all duration-300"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    initial={{ opacity: 0, scale: 0 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: 2.0 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="text-white text-xl">▶️</span>
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>

                    {/* Demo CTA Section */}
                    <motion.div className="mt-4 sm:mt-6 md:mt-8 text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                        <div className="bg-amber-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-amber-200/80">
                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-900 mb-2 sm:mb-4">Ready to Experience This Power?</h3>
                            <p className="text-stone-600 text-sm sm:text-base mb-4 sm:mb-6 max-w-2xl mx-auto px-1">
                                Join thousands of restaurants already using RestroAI to streamline operations, 
                                boost revenue, and delight customers with AI-powered insights.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center">
                                <Link to="/contact-us" className="w-full sm:w-auto text-center bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg shadow-amber-500/25 inline-block">Start Free Trial</Link>
                                <Link to="/login" className="w-full sm:w-auto text-center border-2 border-stone-200 text-stone-700 px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 rounded-full font-semibold text-sm sm:text-base hover:bg-stone-100 transition-all duration-300 inline-block">View Live Demo</Link>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-10 sm:py-14 md:py-20 relative">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <motion.div className="text-center mb-8 sm:mb-12 md:mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-3 sm:mb-6">What Our Customers Say</h2>
                        <p className="text-sm sm:text-base md:text-xl text-stone-600 max-w-2xl mx-auto px-1">Join hundreds of restaurants that have transformed their operations with RestroAI</p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -4 }}
                                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-stone-200 shadow-sm hover:shadow-lg hover:border-amber-100 transition-all duration-300 group"
                            >
                                <div className="flex items-center mb-3 sm:mb-4 md:mb-6">
                                    <div className="text-2xl sm:text-3xl md:text-4xl mr-3 sm:mr-4 group-hover:scale-110 transition-transform duration-300">{testimonial.avatar}</div>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-stone-900 text-sm sm:text-base">{testimonial.name}</div>
                                        <div className="text-stone-500 text-xs sm:text-sm truncate">{testimonial.role}</div>
                                        <div className="flex mt-0.5 sm:mt-1">{[...Array(testimonial.rating)].map((_, i) => (<span key={i} className="text-amber-400 text-xs sm:text-sm">⭐</span>))}</div>
                                    </div>
                                </div>
                                <p className="text-stone-600 mb-3 sm:mb-4 md:mb-6 italic leading-relaxed text-xs sm:text-sm">"{testimonial.content}"</p>
                                <motion.div className="w-full h-0.5 sm:h-1 bg-gradient-to-r from-amber-400 to-amber-500 rounded-full" initial={{ width: 0 }} whileInView={{ width: "100%" }} transition={{ duration: 0.5 }} viewport={{ once: true }} />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-10 sm:py-14 md:py-20 relative">
                <div className="absolute inset-0 bg-amber-50/50" />
                <div className="relative max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
                    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-amber-100 border border-amber-200 mb-3 sm:mb-6">
                            <span className="text-base sm:text-xl md:text-2xl mr-1.5 sm:mr-2">🎯</span>
                            <span className="text-stone-700 font-medium text-xs sm:text-sm md:text-base">Ready to Transform Your Restaurant?</span>
                        </motion.div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-3 sm:mb-6">Start Your AI Journey Today</h2>
                        <p className="text-sm sm:text-base md:text-xl text-stone-600 mb-4 sm:mb-6 md:mb-8 px-1">Join the AI revolution in restaurant management. Start your free trial today and see the difference.</p>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center">
                            <Link to="/contact-us" className="w-full sm:w-auto text-center bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-full font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-xl inline-block">Start Free Trial</Link>
                            <Link to="/login" className="w-full sm:w-auto text-center border-2 border-stone-200 text-stone-700 px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-full font-semibold text-sm sm:text-base md:text-lg hover:bg-stone-100 transition-all duration-300 inline-flex items-center justify-center">🚀 Login to Dashboard →</Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t border-stone-200 py-8 sm:py-10 md:py-12">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8">
                        <div>
                            <img src="/logo-bright.svg" alt="RestroAI" className="h-6 sm:h-8 w-auto mb-3 sm:mb-4" />
                            <p className="text-stone-500 text-xs sm:text-sm leading-relaxed">
                                AI-powered restaurant management platform that revolutionizes how restaurants operate, 
                                analyze data, and serve customers with unprecedented efficiency and insights.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-stone-900 font-semibold mb-2 sm:mb-4 text-sm sm:text-base">Product</h3>
                            <ul className="space-y-1 sm:space-y-2 text-stone-500 text-xs sm:text-sm">
                                <li><a href="#features" className="hover:text-stone-900 transition-colors">Features</a></li>
                                <li><Link to="/pocketbillingai" className="hover:text-stone-900 transition-colors">PocketBillingAI</Link></li>
                                <li><Link to="/restaurants" className="hover:text-stone-900 transition-colors">Restaurants</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-stone-900 font-semibold mb-2 sm:mb-4 text-sm sm:text-base">Company</h3>
                            <ul className="space-y-1 sm:space-y-2 text-stone-500 text-xs sm:text-sm">
                                <li><a href="#about" className="hover:text-stone-900 transition-colors">About</a></li>
                                <li><Link to="/blog" className="hover:text-stone-900 transition-colors">Blog</Link></li>
                                <li><Link to="/careers" className="hover:text-stone-900 transition-colors">Careers</Link></li>
                                <li><Link to="/contact-us" className="hover:text-stone-900 transition-colors">Contact</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-stone-900 font-semibold mb-2 sm:mb-4 text-sm sm:text-base">Support</h3>
                            <ul className="space-y-1 sm:space-y-2 text-stone-500 text-xs sm:text-sm">
                                <li><Link to="/contact-us" className="hover:text-stone-900 transition-colors">Contact Support</Link></li>
                                <li>
                                    <a href="tel:+917881110279" className="hover:text-stone-900 transition-colors font-medium text-stone-700">
                                        +91 7881110279
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-stone-200 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center">
                        <p className="text-stone-600 text-sm sm:text-base font-medium mb-2">
                            <a href="tel:+917881110279" className="hover:text-amber-600 transition-colors">
                                📞 +91 7881110279
                            </a>
                        </p>
                        <p className="text-stone-500 text-xs sm:text-sm">
                            © {new Date().getFullYear()} RestroAI. All rights reserved. | 
                            <Link to="/privacy-policy" className="hover:text-stone-900 transition-colors ml-2">Privacy Policy</Link> | 
                            <Link to="/terms-of-service" className="hover:text-stone-900 transition-colors ml-2">Terms of Service</Link>
                        </p>
                    </div>
                </div>
            </footer>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }
                .animate-pulse {
                    animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
                .animation-delay-1000 {
                    animation-delay: 1s;
                }
            `}</style>
            
            {/* Demo Video Modal */}
            <DemoVideo 
                isOpen={showDemoVideo} 
                onClose={() => setShowDemoVideo(false)} 
            />

            {/* QR Scanner Modal */}
            <QRScanner 
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleQRScan}
            />
        </div>
    );
};

export default Home;
